import { getLogger } from '@packages/core/logging';
import { useEffect, useRef } from 'react';

import { useFeatureFlags } from '../feature-flags';
import { useRuntimeContext } from '../runtime';

/**
 * Headless bridge feeding React state into the logger's ambient layers so
 * every entry carries the runtime context and current feature flags. Lazy
 * resolvers read latest-value refs, so nothing re-registers per render.
 * Mount once inside `FeatureFlagProvider`.
 */
export function LoggingContextBridge(): null {
    const runtimeContext = useRuntimeContext();
    const featureFlags = useFeatureFlags();
    const runtimeRef = useRef(runtimeContext);
    const flagsRef = useRef(featureFlags);

    runtimeRef.current = runtimeContext;
    flagsRef.current = featureFlags;

    useEffect(() => {
        const rootLogger = getLogger();
        const disposeRuntime = rootLogger.setContextLayer('runtime', () => ({
            ...runtimeRef.current,
        }));
        const disposeFlags = rootLogger.setContextLayer('flags', () => ({ ...flagsRef.current }));

        return () => {
            disposeRuntime();
            disposeFlags();
        };
    }, []);

    return null;
}
